import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Wrench, CalendarCheck, Truck, Check, CheckCheck, Trash2, Loader2, Bell, Settings2 } from 'lucide-react';
import { assistAPI } from '../services/api';
import { i18n, useT } from '../services/i18n';
import { notifText } from './NotificationsBell';
import { NotificationPrefs } from './NotificationPrefs';

const KINDS = ['all', 'fitting', 'delivery', 'diagnostic', 'maintenance'];
const ICON = { maintenance: [Wrench, 'bg-blue-100 text-blue-700'], fitting: [CalendarCheck, 'bg-emerald-100 text-emerald-700'], diagnostic: [Camera, 'bg-orange-100 text-orange-700'], delivery: [Truck, 'bg-sky-100 text-sky-700'] };

const Row = ({ n, t, lang, onRead, onDelete }) => {
  const { title, body } = notifText(n, t, lang);
  const [Icon, cls] = ICON[n.kind] || ICON.diagnostic;
  return (
    <li className={`flex gap-3 p-4 ${n.read ? 'bg-white' : 'bg-orange-50/60'}`} data-testid={`nc-item-${n.id}`}>
      <span className={`mt-0.5 shrink-0 rounded-full p-2 ${cls}`}><Icon size={15} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <Link to={n.url || '/mon-compte'} onClick={() => !n.read && onRead(n, true)} className="text-sm font-semibold text-gray-900 hover:text-orange-600 flex-1" data-testid={`nc-title-${n.id}`}>{title}</Link>
          {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-orange-500 shrink-0" data-testid={`nc-unread-${n.id}`} />}
        </div>
        <p className="text-sm text-gray-600 mt-0.5" data-testid={`nc-body-${n.id}`}>{body}</p>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-400">
          <span>{i18n.formatDateTime(n.created_at)}</span>
          <button type="button" onClick={() => onRead(n, !n.read)} className="flex items-center gap-1 hover:text-gray-700" data-testid={`nc-toggle-read-${n.id}`}>
            <Check size={12} /> {n.read ? t('nc.markUnread') : t('nc.markRead')}
          </button>
          <button type="button" onClick={() => onDelete(n)} className="flex items-center gap-1 hover:text-red-600" data-testid={`nc-delete-${n.id}`}><Trash2 size={12} /> {t('nc.delete')}</button>
        </div>
      </div>
    </li>
  );
};

// "All my notifications": filters by type, unread first, per-item read/unread + delete, pagination, e-mail preferences
export const NotificationsCenter = () => {
  const { t, lang } = useT();
  const [kind, setKind] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);

  const load = useCallback(() => assistAPI.notifications({ kind: kind === 'all' ? '' : kind, unread: unreadOnly, page, limit: 15 }).then(setData).catch(() => setData({ items: [], total: 0, pages: 1, unread: 0, counts: {} })), [kind, unreadOnly, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [kind, unreadOnly]);

  const onRead = async (n, read) => { await assistAPI.setNotificationRead(n.id, read).catch(() => {}); load(); };
  const onDelete = async (n) => { await assistAPI.deleteNotification(n.id).catch(() => {}); load(); };
  const readAll = async () => { await assistAPI.markRead().catch(() => {}); load(); };

  const countOf = (k) => (k === 'all' ? Object.values(data?.counts || {}).reduce((a, b) => a + b, 0) : data?.counts?.[k] || 0);

  return (
    <div className="space-y-5" data-testid="notifications-center">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-2 flex-1" role="tablist">
          {KINDS.map((k) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)} data-testid={`nc-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${kind === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:border-gray-400'}`}>
              {t(`nc.kind.${k}`)} <span className={`ml-1 text-xs ${kind === k ? 'text-gray-300' : 'text-gray-400'}`}>{countOf(k)}</span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="nc-unread-only" /> {t('nc.unreadOnly')}</label>
        <button type="button" onClick={readAll} disabled={!data?.unread} className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 disabled:text-gray-400" data-testid="nc-read-all"><CheckCheck size={15} /> {t('nc.readAll')}</button>
        <button type="button" onClick={() => setShowPrefs((s) => !s)} className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900" data-testid="nc-toggle-prefs"><Settings2 size={15} /> {t('nc.prefs')}</button>
      </div>

      {showPrefs && <NotificationPrefs />}

      <div className="bg-white rounded-xl border overflow-hidden">
        {!data && <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-orange-500" /></div>}
        {data && data.items.length === 0 && (
          <div className="p-10 text-center text-gray-500" data-testid="nc-empty"><Bell className="mx-auto mb-2 text-gray-300" size={32} /><p className="text-sm">{t('nc.empty')}</p></div>
        )}
        {data && data.items.length > 0 && <ul className="divide-y">{data.items.map((n) => <Row key={n.id} n={n} t={t} lang={lang} onRead={onRead} onDelete={onDelete} />)}</ul>}
      </div>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="nc-pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded border disabled:opacity-40" data-testid="nc-prev">{t('nc.prev')}</button>
          <span className="text-gray-500">{t('nc.pageOf', { page, pages: data.pages })}</span>
          <button type="button" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded border disabled:opacity-40" data-testid="nc-next">{t('nc.next')}</button>
        </div>
      )}
    </div>
  );
};

export default NotificationsCenter;
